import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { en } from './translations/en';
import { pt } from './translations/pt';

type Language = 'en' | 'pt';

type Translations = typeof en;

type I18nContextValue = {
	language: Language;
	setLanguage: (lang: Language) => void;
	t: Translations;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const translations: Record<Language, Translations> = {
	en,
	pt
};

const LANGUAGE_STORAGE_KEY = 'beerai-language';

export const I18nProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const [language, setLanguageState] = useState<Language>(() => {
		const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
		return (stored === 'pt' || stored === 'en') ? stored : 'en';
	});

	const setLanguage = (lang: Language) => {
		setLanguageState(lang);
		localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
	};

	useEffect(() => {
		localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
	}, [language]);

	const value = useMemo<I18nContextValue>(
		() => ({
			language,
			setLanguage,
			t: translations[language]
		}),
		[language]
	);

	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextValue => {
	const context = useContext(I18nContext);
	if (!context) {
		throw new Error('useI18n must be used within I18nProvider');
	}
	return context;
};
